(ns practitest.frontend.components.project-assistant.actions
  "This namespace implements the 'pending action' pattern: when the LLM calls
  a tool that isn't read-only, the frontend doesn't execute it immediately.
  Instead, it pushes a pending-action message into the chat, which renders
  as a confirmation card with Confirm/Dismiss buttons. When the user clicks
  Confirm, the action runs and its result is reported back to the assistant
  service as a tool-result.

  Two multimethods drive per-tool behavior, both dispatched on tool name:
    - `render-action-card` : how the card body looks for this tool
    - `execute-action`     : what happens when the user confirms

  To add a new tool that needs confirmation (write / soft-action):
    1. Add it to 'tools.cljs' `catalog` (as usual)
    2. Add a `{:category :write}` (or `:soft-action`) entry in 'tools.cljs' `metadata`
    3. Add a `(defmethod render-action-card \"my_tool\" ...)` here
    4. Add a `(defmethod execute-action \"my_tool\" ...)` here

  To add a new read tool (auto-executes, no confirmation):
    1. Add it to 'tools.cljs' `catalog`
    2. Add a `{:category :read}` entry in 'tools.cljs' `metadata`
    3. Add a `(defmethod render-tool-call-message \"my_tool\" ...)` here 
       (optional, falls back to \"Running <name>\")
    4. Add the fetcher to 'events.cljs' `::execute-tools!` switch

  All five customization points (catalog, metadata, two card multimethods,
  tool-call message multimethod) live in this file or 'tools.cljs'."
  (:require
    [practitest.frontend.common-entities.metadata :as md]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common.re-frame :as rf-common]
    [practitest.frontend.components.project-assistant.events :as-alias project-assistant.events]
    [practitest.frontend.components.view-editor.events :as-alias view-editor.events]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; --- card renderers ---

;; NOTE: Each defmethod returns the "body" of the card - everything except the
;;       confirm/dismiss buttons, which `pending-action-bubble` adds automatically.
;;       The message map is passed in full so renderers can access `:args`, `:status`, `:result`, etc.

(defmulti render-action-card
  "Returns hiccup for the body of a pending-action card, given the full pending-action message.
  Dispatched on tool name."
  :tool-name)

(defmethod render-action-card :default
  [{:keys [tool-name]}]
  [:div.action-card-body.is-unknown "Unknown action: " tool-name])

(defmethod render-action-card "navigate_to_grid"
  [{:keys [args]}]
  [:div.action-card-body.is-navigate
   [:div.action-title
    (get-translation "assistant.action.nav.title" "Open in grid")]
   [:div.action-detail
    [:span.entity-type (str (:entity_type args))]
    (when-let [query (:query args)]
      [:code.action-query query])]])

(defmethod render-action-card "navigate_to_view"
  [{:keys [args]}]
  [:div.action-card-body.is-navigate
   [:div.action-title
    (get-translation "assistant.action.open-view.title" "Open custom view")]
   [:div.action-detail
    [:span.entity-type (str (:entity_type args))]
    [:code.action-query (or (:view_name args)
                            (str "View #" (:view_id args)))]]])

(defmethod render-action-card "create_custom_view"
  [{:keys [args]}]
  [:div.action-card-body.is-create-view
   [:div.action-title
    (get-translation "assistant.action.create-view.title" "Create custom view")]
   [:div.action-detail
    [:div.action-row
     [:span.action-label
      (get-translation "assistant.action.create-view.under" "Under:")]
     [:span (or (:parent_path args)
                (get-translation "assistant.action.create-view.root" "(top level)"))]]
    [:div.action-row
     [:span.action-label
      (get-translation "assistant.action.create-view.name" "Name:")]
     [:span (str (:name args))]]
    (when-let [ptql (:view_ptql args)]
      [:div.action-row
       [:span.action-label
        (get-translation "assistant.action.create-view.filter" "Filter:")]
       [:code.action-query ptql]])]])

(defmulti confirm-label
  "Returns the confirm-button text for a pending-action tool. Dispatched on tool name."
  :tool-name)

(defmethod confirm-label :default [_]
  (get-translation "assistant.action.confirm" "Confirm"))

(defmethod confirm-label "create_custom_view" [_]
  (get-translation "assistant.action.create-view.cta" "Open editor"))

;; --- tool-call info messages (read tools) ---

;; NOTE: When a read tool runs inline, we show an information message in
;;       the chat so the user sees what's happening: 
;;       "Searching tests: `Status = \"Open\"`" while the query runs.
;;       Each read tool can register a renderer here.
;;       Falls back to "Running <name>" if no method is defined.

(defmulti render-tool-call-message
  "Returns the `:content` string for a tool-call info message, given the tool call (with `:name` and `:args`).
  Dispatched on `:name`."
  :name)

(defmethod render-tool-call-message :default [{:keys [name]}]
  (str "Running " name))

(defmethod render-tool-call-message "get_field_values" [{:keys [args]}]
  (str "Looking up values for: `" (:field_name args) "`"))

(defmethod render-tool-call-message "ptql_query" [{:keys [args]}]
  (let [entity (md/entity-by-module (:entity_type args))
        plural (get-in md/entities-metadata [entity :plural-name :default])]
    (str "Searching " plural ": `" (:query args) "`")))

(defmethod render-tool-call-message "find_matching_views" [{:keys [args]}]
  ;; TODO: show in what entity
  (str "Finding matching views for: `" (:query args) "`"))

;; --- action handlers ---

;; NOTE: When a user confirms a pending action, execute-action is called with the tool name,
;;       the args that came with the tool call, and the current db.
;;       It returns a map describing what should happen:
;;
;;         `{:dispatch [:event ...]}`     ; re-frame event to dispatch (synchronous)
;;         `{:promise js-promise}`        ; async work to await (e.g. Rails API call)
;;         `{:result "..."}`              ; synchronous string to report back to LLM
;;
;;       The keys compose - a handler can return both `:dispatch` and `:result`.

(defmulti execute-action
  "Runs a confirmed action. Dispatched on tool name. Returns an execution map.
  Never performs IO directly - all side effects go through re-frame effects or js promises."
  (fn [tool-name _args _db] tool-name))

(defmethod execute-action :default
  [tool-name _args _db]
  {:result (str "Unknown action: " tool-name)})

(defmethod execute-action "navigate_to_grid"
  [_ args _db]
  {:dispatch [::project-assistant.events/activate-ptql
              (md/entity-by-module (:entity_type args)) (:query args)]})

(defmethod execute-action "navigate_to_view"
  [_ args _db]
  {:dispatch [::project-assistant.events/activate-view
              (md/entity-by-module (:entity_type args)) (:view_id args)]})

(defmethod execute-action "create_custom_view"
  [_ args _db]
  {:dispatch [::view-editor.events/open-create
              {:parent-id (:parent_id args)
               :entity-type (:entity_type args)
               :seed {:name (:name args)
                      :view-ptql (:view_ptql args)}}]})

;; --- utils ---

(defn- find-pending-msg-idx [db target-id]
  (first (keep-indexed
           (fn [idx {:keys [role tool-call-id]}]
             (when (and (= "pending-action" role)
                        (= target-id tool-call-id))
               idx))
           (get-in db [:project-assistant :messages]))))

;; --- events ---

(re-frame/reg-event-fx
  ::confirm-action
  (fn [{db :db} [_ tool-call-id]]
    (if-let [msg-idx (find-pending-msg-idx db tool-call-id)]
      (let [msg (get-in db [:project-assistant :messages msg-idx])
            {:keys [tool-name args id]} msg
            execution (execute-action tool-name args db)
            new-db (assoc-in db [:project-assistant :messages msg-idx :status] :confirmed)
            project-id (subs/get-current-project-id db)
            convo-id (get-in db [:project-assistant :conversation :id])
            patch-data (cond-> {:status "confirmed"}
                         (:result execution) (assoc :result (:result execution)))]
        (mixpanel/track "Assistant Action Confirmed" {"tool" tool-name})
        (cond-> {:db new-db}
          (:dispatch execution)
          (assoc :dispatch (:dispatch execution))

          (and id convo-id)
          (assoc ::rf-common/http
                 {:url (broutes/get-url "assistant_messages#update"
                                        {:p_id project-id
                                         :assistant_conversation_id convo-id
                                         :id id})
                  :method "PATCH"
                  :body {:data patch-data}})))
      {})))

(re-frame/reg-event-fx
  ::dismiss-action
  (fn [{db :db} [_ tool-call-id]]
    (if-let [msg-idx (find-pending-msg-idx db tool-call-id)]
      (let [{:keys [tool-name id]} (get-in db [:project-assistant :messages msg-idx])
            project-id (subs/get-current-project-id db)
            convo-id (get-in db [:project-assistant :conversation :id])
            patch-data {:status "dismissed"}]
        (mixpanel/track "Assistant Action Dismissed" {"tool" tool-name})
        (cond-> {:db (assoc-in db [:project-assistant :messages msg-idx :status] :dismissed)}
          (and id convo-id)
          (assoc ::rf-common/http
                 {:url (broutes/get-url "assistant_messages#update"
                                        {:p_id project-id
                                         :assistant_conversation_id convo-id
                                         :id id})
                  :method "PATCH"
                  :body {:data patch-data}})))
      {})))

;; --- pending-action bubble ---

(defn pending-action-bubble
  "Renders a pending-action message in the chat.

  Expected message shape:
    {:role \"pending-action\"
     :tool-call-id \"call_abc_123\"
     :tool-name \"navigate_to_grid\"
     :args {...}
     :status :pending | :confirmed | :dismissed | :failed
     :result <optional, set after execution>}"
  [{:keys [tool-call-id status result] :as msg}]
  [:div.pending-action
   {:class (str "is-" (name status))}
   [render-action-card msg]
   (case status
     :pending
     [:div.pending-action-buttons
      [buttons/button
       {:primary? true
        :text (confirm-label msg)
        :on-click #(re-frame/dispatch [::confirm-action tool-call-id])}]
      [buttons/button
       {:text (get-translation "assistant.action.dismiss" "Dismiss")
        :on-click #(re-frame/dispatch [::dismiss-action tool-call-id])}]]

     :confirmed
     [:div.pending-action-status
      (get-translation "assistant.action.confirmed" "Done")]

     :dismissed
     [:div.pending-action-status
      (get-translation "assistant.action.dismissed" "Dismissed")]

     :failed
     [:div.pending-action-status.is-error (str result)])])
